import { useEffect, useMemo, useState } from "react";
import { Drawer, Flex, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";

import SearchPanel from "./SearchPanel";
import ObjectPanel from "./ObjectPanel";
import ChevronDownIcon from "@/icons/ChevronDownIcon";
import ChevronUpIcon from "@/icons/ChevronUpIcon";

import styles from "./right-drawer.module.css";

const getViewportHeight = () => {
  if (typeof window === "undefined") {
    return 0;
  }

  return Math.ceil(window.visualViewport?.height || window.innerHeight);
};

const MobileRightPanelDrawer = ({
  isOpen,
  onClose,
  isObjectOpen,
  currentObject,
  isObjectLoading,
  isResultsLoading,
  searchValue,
  searchInSelectedDefinitions,
  onSearchInSelectedDefinitionsChange,
  isClickResult = false,
  onCancelMapClick,
  onSearch,
  onApplySearch,
  searchResults = [],
  onSelectObject,
  definitionValue,
  definitionsOptions,
  onDefinitionChange,
  isDefinitionRequired,
  isLoading = false,
  onMinimizedChange,
}) => {
  const [activeTabKey, setActiveTabKey] = useState();
  const [areResultsVisible, setAreResultsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(getViewportHeight);

  const { definition } = currentObject || {};

  const groupsFields = useMemo(() => {
    return (
      definition?.groups
        ?.filter(({ fields }) => !!fields && !!fields.length)
        .sort((a, b) => a.idx - b.idx) || []
    );
  }, [definition]);

  useEffect(() => {
    if (groupsFields?.length) {
      setActiveTabKey(groupsFields[0].uuid);
    }
  }, [definition?.uuid, groupsFields]);

  useEffect(() => {
    setAreResultsVisible(!isObjectOpen);
  }, [isObjectOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsMinimized(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isResultsLoading) {
      setIsMinimized(false);
    }
  }, [isResultsLoading]);

  useEffect(() => {
    if (isClickResult && searchResults?.length > 0) {
      setIsMinimized(false);
      setAreResultsVisible(true);
    }
  }, [isClickResult, searchResults]);

  useEffect(() => {
    onMinimizedChange?.(isMinimized);
  }, [isMinimized, onMinimizedChange]);

  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(getViewportHeight());
    };

    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    window.visualViewport?.addEventListener("resize", updateViewportHeight);

    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      window.visualViewport?.removeEventListener("resize", updateViewportHeight);
    };
  }, []);

  const handleApplySearch = (items) => {
    onApplySearch(items);
    setIsMinimized(true);
  };

  const drawerSize = isMinimized ? 64 : viewportHeight;

  return (
    <Drawer
      closable={false}
      destroyOnHidden
      onClose={onClose}
      open={isOpen}
      placement="bottom"
      size={drawerSize}
      mask={false}
      maskClosable={false}
      className={`${styles.mobile_drawer} ${
        isMinimized ? styles.mobile_drawer_minimized : ""
      }`}
      styles={{
        body: {
          padding: isMinimized ? 12 : 16,
          overflow: "hidden",
        },
      }}
      extra={null}
    >
      <Button
        size="large"
        className={styles.mobile_minimize}
        icon={isMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />}
        onClick={() => setIsMinimized((prev) => !prev)}
      />

      <Button
        size="large"
        className={styles.mobile_close}
        icon={<CloseOutlined />}
        onClick={onClose}
      />

      {!isMinimized && (
        <Flex vertical gap={16} className={styles.mobile_content}>
          {isObjectOpen ? (
            <>
              <SearchPanel
                className={styles.mobile_search_controls}
                onSearch={(value) => {
                  setAreResultsVisible(true);
                  onSearch(value);
                }}
                onApplySearch={handleApplySearch}
                searchResults={searchResults}
                onSelectObject={(item) => {
                  setAreResultsVisible(false);
                  onSelectObject(item);
                }}
                definitionValue={definitionValue}
                definitionsOptions={definitionsOptions}
                onDefinitionChange={(value) => {
                  setAreResultsVisible(true);
                  onDefinitionChange(value);
                }}
                isDefinitionRequired={isDefinitionRequired}
                searchValue={searchValue}
                searchInSelectedDefinitions={searchInSelectedDefinitions}
                onSearchInSelectedDefinitionsChange={
                  onSearchInSelectedDefinitionsChange
                }
                isClickResult={isClickResult}
                onCancelMapClick={onCancelMapClick}
                showResultsToggle
                areResultsVisible={areResultsVisible}
                onToggleResults={() => setAreResultsVisible((prev) => !prev)}
                hideResults
              />

              <div className={styles.mobile_body}>
                <ObjectPanel
                  activeTabKey={activeTabKey}
                  setActiveTabKey={setActiveTabKey}
                  currentObject={currentObject}
                  isLoading={isObjectLoading}
                />

                {areResultsVisible && (
                  <div className={styles.mobile_results_sheet}>
                    <SearchPanel
                      className={styles.mobile_results_panel}
                      onSearch={(value) => {
                        setAreResultsVisible(true);
                        onSearch(value);
                      }}
                      onApplySearch={handleApplySearch}
                      searchResults={searchResults}
                      onSelectObject={(item) => {
                        setAreResultsVisible(false);
                        onSelectObject(item);
                      }}
                      definitionValue={definitionValue}
                      definitionsOptions={definitionsOptions}
                      onDefinitionChange={(value) => {
                        setAreResultsVisible(true);
                        onDefinitionChange(value);
                      }}
                      isDefinitionRequired={isDefinitionRequired}
                      searchValue={searchValue}
                      searchInSelectedDefinitions={searchInSelectedDefinitions}
                      onSearchInSelectedDefinitionsChange={
                        onSearchInSelectedDefinitionsChange
                      }
                      isClickResult={isClickResult}
                      onCancelMapClick={onCancelMapClick}
                      isLoading={isLoading || isResultsLoading}
                      hideControls
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <SearchPanel
              className={styles.mobile_full_search}
              onSearch={onSearch}
              onApplySearch={handleApplySearch}
              searchResults={searchResults}
              onSelectObject={(item) => {
                setAreResultsVisible(false);
                onSelectObject(item);
              }}
              definitionValue={definitionValue}
              definitionsOptions={definitionsOptions}
              onDefinitionChange={onDefinitionChange}
              isDefinitionRequired={isDefinitionRequired}
              searchValue={searchValue}
              searchInSelectedDefinitions={searchInSelectedDefinitions}
              onSearchInSelectedDefinitionsChange={
                onSearchInSelectedDefinitionsChange
              }
              isClickResult={isClickResult}
              onCancelMapClick={onCancelMapClick}
              isLoading={isLoading || isResultsLoading}
            />
          )}
        </Flex>
      )}
    </Drawer>
  );
};

export default MobileRightPanelDrawer;
